import { calendarIcons } from '../../../shared/constants';

/**
 * Shared option building + search for the exercise pickers
 * (SelectDropdownExercisePicker and MobileAddExercisePicker).
 *
 * Accounts can have 15k+ exercises. Each picker used to map the full list into
 * its own option objects on every render, and Build mounts many pickers at
 * once (one per month-view day cell, one per day/week add row, one per item
 * card for swap) - on large accounts that held ~1M option objects and ran the
 * tab out of memory. Building once per allExercises array and sharing the
 * result keeps a single copy regardless of how many pickers exist.
 */

/** type -> calendarIcons entry. Keeps the FIRST entry for a type (there are two
 * 'H' entries) to match the `.find()` lookup this replaces. */
const iconMetaByType = new Map();
Object.values(calendarIcons).forEach((iconObj) => {
  if (!iconMetaByType.has(iconObj.type)) iconMetaByType.set(iconObj.type, iconObj);
});

export const getIconMetaForType = (type) => iconMetaByType.get(type);

/** allExercises array -> Map(typeToFilter key -> options). Keyed on array
 * identity, so a refetch or newly created exercise (both produce a new array)
 * rebuilds once, and the old array's options are garbage-collected with it. */
const optionsCache = new WeakMap();

/**
 * Exercise options for a picker, optionally limited to one exercise type (swap
 * pickers only offer same-type exercises). Returns the same array for the same
 * inputs, so callers must treat it as read-only.
 */
export const getExerciseOptions = (allExercises, typeToFilter = null) => {
  if (!Array.isArray(allExercises)) return [];
  let byType = optionsCache.get(allExercises);
  if (!byType) {
    byType = new Map();
    optionsCache.set(allExercises, byType);
  }
  const cacheKey = typeToFilter || '__all__';
  if (!byType.has(cacheKey)) {
    const options = allExercises
      .filter((exercise) => (typeToFilter ? exercise.type === typeToFilter : true))
      .map((exercise) => {
        const meta = getIconMetaForType(exercise.type);
        return {
          ...exercise,
          name: exercise.name,
          exerciseId: exercise.id,
          icon: meta?.icon,
          iconColor: meta?.iconColor,
          squareIconContainer: true,
          iconBackgroundColor: meta?.containerColor,
        };
      });
    byType.set(cacheKey, options);
  }
  return byType.get(cacheKey);
};

const PRIORITY_TYPES = ['video', 'session_break', 'rest'];

const normalize = (str) => (str || '').toLowerCase().trim();

/** How directly a (normalized) name matches the query; lower ranks first. */
const getRelevanceScore = (name, query) => {
  if (name === query) return 0;
  if (name.startsWith(query)) return 1;
  if (name.split(/\s+/).some((word) => word.startsWith(query))) return 2;
  if (name.includes(query)) return 3;
  return 4;
};

/**
 * Filter options to those whose name contains every word of the query, with
 * quick-add items (video, session break, rest) pinned to the top. With a query,
 * the rest are ranked by relevance and then alphabetically; with no query they
 * keep their source order.
 */
export const searchExerciseOptions = (options, searchTerm) => {
  const query = normalize(searchTerm);
  const searchWords = query.split(/\s+/).filter(Boolean);

  const filtered = options.filter((obj) => {
    const name = normalize(obj.name);
    return searchWords.every((word) => name.includes(word));
  });

  const priorityItems = filtered.filter((item) => PRIORITY_TYPES.includes(item.type));
  const normalItems = filtered.filter((item) => !PRIORITY_TYPES.includes(item.type));

  const sortedNormalItems = query
    ? [...normalItems].sort((a, b) => {
      const nameA = normalize(a.name);
      const nameB = normalize(b.name);
      const scoreA = getRelevanceScore(nameA, query);
      const scoreB = getRelevanceScore(nameB, query);
      if (scoreA !== scoreB) return scoreA - scoreB;
      return nameA.localeCompare(nameB);
    })
    : normalItems;

  return [...priorityItems, ...sortedNormalItems];
};
